import { inject, Injectable } from '@angular/core';
import { AuthService, BackendPrice, Customer, CustomerProductPrice } from 'gung-standard';
import { JeevesPriceService } from 'gung-standard-jeeves';
import { filter, first, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { CarLinerMysetupService, MySetup } from './car-liner-mysetup.service';

@Injectable({
  providedIn: 'root'
})
export class CarLinerPriceService extends JeevesPriceService {
  // End users don't see prices of products worth this much or more (SEK), a "contact your sales representative" text is shown instead
  protected contactSalesRepPriceLimit = 200000;
  protected isEndUser = false;
  protected carLinerMysetupService = inject(CarLinerMysetupService);

  protected rolesSubscription = inject(AuthService)
    .getRoles()
    .pipe(filter(roles => !!roles))
    .subscribe(roles => (this.isEndUser = roles.includes('END_USER')));

  getCustomerPrice(productId: string, customerId: string): Observable<CustomerProductPrice> {
    return this.internalHttpGetCustomerPrice(productId, customerId).pipe(
      mergeMap(x =>
        forkJoin([
          of(x),
          this.customerService.getSelectedCustomer().pipe(first()),
          this.carLinerMysetupService.getMySetup().pipe(first())
        ])
      ),
      map(([backendPrice, customer, mySetup]) => {
        return this.mapCustomerProductPriceWithSetup(backendPrice, customer, mySetup);
      })
    );
  }

  getCustomerPrices(productIds: string[], customerId: string): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(productIds, customerId).pipe(
      switchMap(x =>
        forkJoin({
          backendPrices: of(x),
          customer: this.customerService.getSelectedCustomer().pipe(first()),
          mySetup: this.carLinerMysetupService.getMySetup().pipe(first())
        })
      ),
      map(({ backendPrices, customer, mySetup }) => {
        return Object.values(backendPrices).map(backendPrice => {
          return this.mapCustomerProductPriceWithSetup(backendPrice, customer, mySetup);
        });
      })
    );
  }

  protected mapCustomerProductPriceWithSetup(
    backendPrice: BackendPrice,
    customer: Customer,
    mySetup: MySetup,
    qty?: number
  ) {
    const priceLevel = this.getPriceLevel(backendPrice, qty);

    const customerDiscountPercent = priceLevel.discount;

    let customerGrossPrice = priceLevel.price ? priceLevel.price / ((100 - customerDiscountPercent) / 100) : 0;
    // Compared before the MySetup currency rate and markup are applied, so the limit is always against the backend price
    const contactSalesRep = this.isEndUser && customerGrossPrice >= this.contactSalesRepPriceLimit;
    let customerNetPrice = priceLevel.price;
    if (mySetup && mySetup.currencyRate) {
      customerNetPrice = customerNetPrice * mySetup.currencyRate;
      customerGrossPrice = customerGrossPrice * mySetup.currencyRate;
    }

    if (mySetup && mySetup.markUp) {
      // customerNetPrice = customerNetPrice + customerNetPrice * (mySetup.markUp / 100);
      customerGrossPrice = customerGrossPrice + customerGrossPrice * (mySetup.markUp / 100);
    }

    let currencyCode = '';
    if (mySetup && mySetup.currency) {
      currencyCode = mySetup.currency;
    } else {
      currencyCode = customer.extra.kus.valkod;
    }

    // Upcoming list price is a gross price, so it gets the same currency rate and markup as customerGrossPrice
    let newPrice: { value: number; currencyCode: string; date: string } = undefined;
    if (priceLevel.extra?.newPrice) {
      let newPriceValue = priceLevel.extra.newPrice.price;
      if (mySetup && mySetup.currencyRate) {
        newPriceValue = newPriceValue * mySetup.currencyRate;
      }
      if (mySetup && mySetup.markUp) {
        newPriceValue = newPriceValue + newPriceValue * (mySetup.markUp / 100);
      }
      newPrice = { value: newPriceValue, currencyCode, date: priceLevel.extra.newPrice.date };
    }

    const customerProductPrice: CustomerProductPrice = {
      productId: backendPrice.productId,
      customerId: customer.id,
      customerDiscountPercent,
      customerGrossPrice: {
        value: customerGrossPrice,
        currencyCode: currencyCode
      },
      customerNetPrice: {
        value: customerNetPrice,
        currencyCode: currencyCode
      },
      customerDiscountAmount: {
        value: customerGrossPrice - customerNetPrice,
        currencyCode: currencyCode
      },
      backendPriceLevel: priceLevel,
      priceFactor: backendPrice.priceFactor,
      backendPrice: backendPrice,
      extra: { contactSalesRep, newPrice }
    };

    return customerProductPrice;
  }

  getCustomerPricesWithQtys(
    input: { [productId: string]: number },
    customerId: string
  ): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(Object.keys(input), customerId).pipe(
      switchMap(resp =>
        forkJoin({
          prices: of(resp),
          customer: this.customerService.getSelectedCustomer().pipe(first()),
          mySetup: this.carLinerMysetupService.getMySetup().pipe(first())
        })
      ),
      map(({ prices, customer, mySetup }) => {
        return Object.values(prices).map(price =>
          this.mapCustomerProductPriceWithSetup(price, customer, mySetup, input[price.productId])
        );
      })
    );
  }

  priceMarkupIsEnabled(): Observable<boolean> {
    return this.carLinerMysetupService.getMySetup().pipe(
      first(),
      map(mySetup => !!(mySetup && mySetup.markUp && mySetup.markUp > 0))
    );
  }
}
